import { motion } from 'framer-motion';
import { Play, Calendar, Clock, Film, Users, Star, Award, Music } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

// Cloudinary images
const IMAGES = {
  aprokashito1: "https://res.cloudinary.com/drcoefnzk/image/upload/v1771512905/469821196_122132213570499699_8876230410064543970_n_mevj6e.jpg",
  aprokashito2: "https://res.cloudinary.com/drcoefnzk/image/upload/v1771512904/469854315_122132242076499699_6419784136414131561_n_lq0k9o.jpg",
  aprokashito3: "https://res.cloudinary.com/drcoefnzk/image/upload/v1771512904/469813353_122132253416499699_89532103118283627_n_ana8w4.jpg",
  gallery1: "https://res.cloudinary.com/drcoefnzk/image/upload/v1771512905/470077372_122132689964499699_3067111797997121261_n_f6uldy.jpg",
  gallery2: "https://res.cloudinary.com/drcoefnzk/image/upload/v1771512905/468335147_122129000006499699_5583324214181612175_n_rii3ua.jpg",
  gallery3: "https://res.cloudinary.com/drcoefnzk/image/upload/v1771512906/470221097_122133958580499699_7036109010119657223_n_hpmlcv.jpg",
  premiere1: "https://res.cloudinary.com/drcoefnzk/image/upload/v1771512906/470266213_122132673044499699_4752606058197197552_n_x012lo.jpg",
  premiere2: "https://res.cloudinary.com/drcoefnzk/image/upload/v1771512906/470261869_122132666054499699_6053191376983885333_n_rvphve.jpg",
  premiere3: "https://res.cloudinary.com/drcoefnzk/image/upload/v1771512906/470124006_122132676740499699_5677850631442857850_n_ibqlbe.jpg",
  audience: "https://res.cloudinary.com/drcoefnzk/image/upload/v1771512908/469915718_122132673578499699_4338502581283027790_n_g70az7.jpg",
  hero: "https://res.cloudinary.com/drcoefnzk/image/upload/v1771512905/469821196_122132213570499699_8876230410064543970_n_mevj6e.jpg",
};

const Films = () => {
  const galleryImages = [
    IMAGES.aprokashito1,
    IMAGES.aprokashito2,
    IMAGES.aprokashito3,
    IMAGES.gallery1,
    IMAGES.gallery2,
    IMAGES.gallery3,
    IMAGES.premiere1,
    IMAGES.premiere2,
  ];

  return (
    <div data-testid="films-page" className="min-h-screen bg-[#050505] pt-20">
      {/* Hero Banner */}
      <section className="relative h-[70vh] flex items-end overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${IMAGES.hero})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/60 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#050505]/80 to-transparent" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pb-16 w-full">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex flex-wrap gap-3 mb-4">
              <span className="px-3 py-1 bg-[#E50914]/20 text-[#E50914] text-xs tracking-wider uppercase rounded">
                Suspense
              </span>
              <span className="px-3 py-1 bg-[#E50914]/20 text-[#E50914] text-xs tracking-wider uppercase rounded">
                Thriller
              </span>
              <span className="px-3 py-1 bg-[#D4AF37]/20 text-[#D4AF37] text-xs tracking-wider uppercase rounded">
                Bengali
              </span>
            </div>

            <h1 className="font-heading text-6xl md:text-8xl text-white tracking-tight mb-4">
              APROKASHITO
            </h1>
            <p className="text-[#D4AF37] font-accent text-xl italic mb-6">অপ্রকাশিত • The Unrevealed</p>

            <div className="flex flex-wrap items-center gap-6 text-[#A3A3A3] mb-8">
              <span className="flex items-center gap-2">
                <Calendar size={16} className="text-[#D4AF37]" />
                September 6, 2024
              </span>
              <span className="flex items-center gap-2">
                <Clock size={16} className="text-[#D4AF37]" />
                1h 50m
              </span>
              <span className="flex items-center gap-2">
                <Film size={16} className="text-[#D4AF37]" />
                Suspense Thriller
              </span>
            </div>

            <div className="flex flex-wrap gap-4">
              <a href="https://youtu.be/eus3JnJAz3g" target="_blank" rel="noopener noreferrer">
                <Button
                  data-testid="watch-trailer-btn"
                  className="bg-[#E50914] hover:bg-[#B20710] text-white px-8 py-6 text-sm tracking-widest uppercase font-semibold rounded-sm"
                >
                  <Play className="mr-2" size={18} fill="white" />
                  Watch Trailer
                </Button>
              </a>
              <a href="https://youtu.be/78vhWIegRto" target="_blank" rel="noopener noreferrer">
                <Button
                  data-testid="watch-teaser-btn"
                  variant="outline"
                  className="border-white/30 text-white hover:bg-white/10 px-8 py-6 text-sm tracking-widest uppercase font-semibold rounded-sm"
                >
                  Watch Teaser
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Film Details */}
      <section className="py-16 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Tabs defaultValue="about" className="w-full">
            <TabsList className="bg-[#121212] border border-white/10 p-1 mb-12">
              <TabsTrigger 
                value="about" 
                className="data-[state=active]:bg-[#E50914] data-[state=active]:text-white px-6 py-3 text-sm tracking-wider uppercase"
              >
                About
              </TabsTrigger>
              <TabsTrigger 
                value="cast" 
                className="data-[state=active]:bg-[#E50914] data-[state=active]:text-white px-6 py-3 text-sm tracking-wider uppercase"
              >
                Cast & Crew
              </TabsTrigger>
              <TabsTrigger 
                value="gallery" 
                className="data-[state=active]:bg-[#E50914] data-[state=active]:text-white px-6 py-3 text-sm tracking-wider uppercase"
              >
                Gallery
              </TabsTrigger>
              <TabsTrigger 
                value="videos" 
                className="data-[state=active]:bg-[#E50914] data-[state=active]:text-white px-6 py-3 text-sm tracking-wider uppercase"
              >
                Videos
              </TabsTrigger>
            </TabsList>

            {/* About Tab */}
            <TabsContent value="about">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
                <div className="lg:col-span-2">
                  <h2 className="font-heading text-3xl text-white mb-6 tracking-wider">SYNOPSIS</h2>
                  <div className="space-y-4 text-[#A3A3A3] leading-relaxed">
                    <p>
                      <span className="font-accent italic text-[#D4AF37]">Aprokashito</span> follows the harrowing 
                      journey of Amit Dutta, a celebrated author whose world crumbles when he faces accusations 
                      of plagiarism. What begins as a professional controversy soon spirals into something far 
                      more sinister.
                    </p>
                    <p>
                      When his ex-wife and journalists dig deeper into his past, dark secrets emerge. A charge 
                      of child murder transforms the literary genius into society's villain. His own autobiography 
                      is weaponized against him, painting a picture so dark that it leads to a life sentence.
                    </p>
                    <p>
                      Director Soham Acharya masterfully weaves a narrative that explores the psychology behind 
                      crime and creation, blurring the lines between fiction and reality. The film challenges 
                      audiences to question what they see and believe.
                    </p>
                  </div>

                  <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-6">
                    <div className="bg-[#121212] border border-white/5 p-6 rounded-lg text-center">
                      <Film className="text-[#D4AF37] mx-auto mb-3" size={24} />
                      <p className="text-xs text-[#A3A3A3] uppercase tracking-wider mb-1">Genre</p>
                      <p className="text-white font-semibold">Suspense Thriller</p>
                    </div>
                    <div className="bg-[#121212] border border-white/5 p-6 rounded-lg text-center">
                      <Calendar className="text-[#D4AF37] mx-auto mb-3" size={24} />
                      <p className="text-xs text-[#A3A3A3] uppercase tracking-wider mb-1">Release</p>
                      <p className="text-white font-semibold">Sep 6, 2024</p>
                    </div>
                    <div className="bg-[#121212] border border-white/5 p-6 rounded-lg text-center">
                      <Clock className="text-[#D4AF37] mx-auto mb-3" size={24} />
                      <p className="text-xs text-[#A3A3A3] uppercase tracking-wider mb-1">Runtime</p>
                      <p className="text-white font-semibold">1h 50m</p>
                    </div>
                    <div className="bg-[#121212] border border-white/5 p-6 rounded-lg text-center">
                      <Star className="text-[#D4AF37] mx-auto mb-3" size={24} />
                      <p className="text-xs text-[#A3A3A3] uppercase tracking-wider mb-1">Language</p>
                      <p className="text-white font-semibold">Bengali</p>
                    </div>
                  </div>
                </div>

                <div>
                  <div className="aspect-[2/3] rounded-lg overflow-hidden film-card">
                    <img
                      src={IMAGES.aprokashito1}
                      alt="Aprokashito Poster"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Cast & Crew Tab */}
            <TabsContent value="cast">
              <div className="space-y-12">
                <div>
                  <h2 className="font-heading text-3xl text-white mb-8 tracking-wider">MAIN CAST</h2>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    {[
                      { name: 'Aryann Bhowmik', role: 'Lead Actor', image: IMAGES.aprokashito2 },
                      { name: 'Joy Sengupta', role: 'Amit Dutta', image: IMAGES.gallery1 },
                      { name: 'Debleena Dutt', role: 'Lead Actress', image: IMAGES.gallery2 },
                    ].map((actor, index) => (
                      <motion.div
                        key={actor.name}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.4, delay: index * 0.1 }}
                        className="cast-card bg-[#121212] border border-white/5 rounded-lg overflow-hidden"
                      >
                        <div className="aspect-[4/5] overflow-hidden">
                          <img
                            src={actor.image}
                            alt={actor.name}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="p-6">
                          <h3 className="text-white font-semibold text-lg">{actor.name}</h3>
                          <p className="text-[#D4AF37] text-sm font-accent italic">{actor.role}</p>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>

                <div>
                  <h2 className="font-heading text-3xl text-white mb-8 tracking-wider">CREW</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {[
                      { name: 'Soham Acharya', role: 'Director' },
                      { name: 'Swarnali Acharya', role: 'Producer' },
                      { name: 'Swarnali Entertainment', role: 'Production House' },
                    ].map((crew, index) => (
                      <div
                        key={crew.name}
                        className="bg-[#121212] border border-white/5 p-6 rounded-lg"
                      >
                        <Award className="text-[#D4AF37] mb-3" size={24} />
                        <h3 className="text-white font-semibold">{crew.name}</h3>
                        <p className="text-[#A3A3A3] text-sm">{crew.role}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Gallery Tab */}
            <TabsContent value="gallery">
              <h2 className="font-heading text-3xl text-white mb-8 tracking-wider">PHOTO GALLERY</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {galleryImages.map((img, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: index * 0.05 }}
                    className="aspect-square overflow-hidden rounded-lg film-card"
                  >
                    <img
                      src={img}
                      alt={`Gallery image ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </motion.div>
                ))}
              </div>
            </TabsContent>

            {/* Videos Tab */}
            <TabsContent value="videos">
              <h2 className="font-heading text-3xl text-white mb-8 tracking-wider">VIDEOS</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Trailer */}
                <div>
                  <div className="video-container rounded-lg overflow-hidden mb-4">
                    <iframe
                      src="https://www.youtube.com/embed/eus3JnJAz3g"
                      title="Aprokashito Official Trailer"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </div>
                  <h3 className="text-white font-semibold text-lg">Official Trailer</h3>
                  <p className="text-[#A3A3A3] text-sm">The gripping first look at the suspense thriller</p>
                </div>

                {/* Teaser */}
                <div>
                  <div className="video-container rounded-lg overflow-hidden mb-4">
                    <iframe
                      src="https://www.youtube.com/embed/78vhWIegRto"
                      title="Aprokashito Teaser"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </div>
                  <h3 className="text-white font-semibold text-lg">Official Teaser</h3>
                  <p className="text-[#A3A3A3] text-sm">A glimpse into the world of Aprokashito</p>
                </div>

                {/* Public Reaction */}
                <div>
                  <div className="video-container rounded-lg overflow-hidden mb-4">
                    <iframe
                      src="https://www.youtube.com/embed/dgtRaLPSmMc"
                      title="Hall Public Reaction"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </div>
                  <h3 className="text-white font-semibold text-lg">Public Reaction</h3>
                  <p className="text-[#A3A3A3] text-sm">Audience reactions from the premiere</p>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* Music Video Section */}
      <section className="py-24 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <span className="text-[#D4AF37] text-sm tracking-[0.3em] uppercase font-semibold">Music Production</span>
            <h2 className="font-heading text-4xl md:text-6xl text-white tracking-wide mt-4">
              MUSIC VIDEO
            </h2>
            <p className="text-[#A3A3A3] mt-4 max-w-2xl mx-auto">
              Produced by Swarnali Acharya • Now streaming on Zee Music Bangla
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative max-w-4xl mx-auto rounded-lg overflow-hidden"
            style={{ boxShadow: '0 0 60px rgba(212, 175, 55, 0.2)' }}
          >
            <div className="video-container">
              <iframe
                src="https://www.youtube.com/embed/op2BJ06feTg"
                title="Music Video - Swarnali Entertainment"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay: 0.2 }}
            className="text-center mt-8"
          >
            <a
              href="https://youtu.be/op2BJ06feTg?si=EvP7zRR3t5FAJYqD"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-[#D4AF37] hover:text-white transition-colors"
            >
              <Play size={16} />
              Watch on YouTube
            </a>
          </motion.div>
        </div>
      </section>

      {/* Coming Soon Section */}
      <section className="py-24 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <span className="inline-block px-4 py-2 bg-[#D4AF37]/20 border border-[#D4AF37]/50 rounded-full text-[#D4AF37] text-sm tracking-widest uppercase mb-6 pulse-glow">
              Announcement Soon
            </span>
            <h2 className="font-heading text-5xl md:text-7xl text-white tracking-tight mb-4">
              HINDI FILM PROJECT
            </h2>
            <p className="text-gold-gradient font-heading text-3xl md:text-4xl">2026</p>
            <p className="text-[#A3A3A3] text-lg max-w-xl mx-auto mt-6">
              Swarnali Entertainment is expanding into Hindi cinema. Details coming soon.
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Films;
